import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiCopy, FiSearch,
} from "react-icons/fi";
import { ethers } from "ethers";
import { toast } from "react-hot-toast";
import { useWallet } from "../hooks/useWallet";
import { useRPCProvider } from "../hooks/useRPCProvider";
import { CONTRACT_ADDRESSES, CONTRACT_ABIS, API_BASE } from "../constants/index";
import { useTranslation } from "../hooks/useTranslation";

import { HiUser, HiUserGroup } from "react-icons/hi2";
import CurveLineImg from "../images/curve-line.png";
import UserProfileIcon from "../images/P2.png";
import PurpleCircleFrameImg from "../images/purple-circle-frame.png";
import background_tree_img from "../images/bg-community-header.png";

// Cached monthIndex to avoid calling getCurrentMonthIndex on every single RPC call
let _cachedMonthIdx = null;
let _cachedMonthIdxTs = 0;
const MONTH_IDX_TTL = 60_000; // 60 seconds

async function getCachedMonthIndex(bonusContract) {
    const now = Date.now();
    if (_cachedMonthIdx != null && now - _cachedMonthIdxTs < MONTH_IDX_TTL) {
        return _cachedMonthIdx;
    }
    const mi = await bonusContract.getCurrentMonthIndex().catch(() => 0);
    _cachedMonthIdx = typeof mi === "bigint" ? Number(mi) : (mi?.toNumber?.() ?? Number(mi)) ?? 0;
    _cachedMonthIdxTs = now;
    return _cachedMonthIdx;
}

// Run async tasks with a concurrency limit to avoid RPC throttling
async function batchAsync(tasks, concurrency = 3) {
    const results = [];
    let idx = 0;
    async function worker() {
        while (idx < tasks.length) {
            const i = idx++;
            results[i] = await tasks[i]();
        }
    }
    await Promise.all(Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker()));
    return results;
}

const GET_TOTAL_STAKING_API = `${API_BASE}/publicApi/getTotalStaking`;
const GET_TOTAL_GROUP_STAKING_API = `${API_BASE}/publicApi/getTotalGroupStaking`;

// const WHITELIST_ADDRESSES = [
//     "0xe62a9e7ef48607b6c3d64bbda5e774eb25e34db7",
//     "0xe29335a2e91cd6504005fe1e964094fdbaa07ef4",
//     "0x5bfa94f953582b8b6d17b650b9d267d939d457aa"
// ];

// const lowerWhitelist = WHITELIST_ADDRESSES.map(addr => addr.toLowerCase());

export default function Community() {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const { account, isConnected, isCorrectNetwork } = useWallet();
    const { executeWithFailover } = useRPCProvider();

    // Refs for accessing latest function references in intervals
    const fetchFunctionsRef = useRef();
    // When true, we're in viewMyOwnCommunity refetching for self; skip the "viewingAddress changed" effect to avoid race
    const isReturningToMyRef = useRef(false);
    // set user profile details and group sales
    const [groupSales, setGroupSales] = useState({
        level: "S0",
        amount: "0",
        address: "",
        totalStake: "0",
        totalMembers: 0,
        totalVolume: "0",
        smallZoneVolume: "0",
        activeMembers: 0,
    });
    const [, setIsLoadingGroupSales] = useState(false);

    // State management
    const [userProfile, setUserProfile] = useState({
        id: "123452",
        level: "S0",
        address: "",
        totalBonus: "0",
        teamInvestment: "0",
        directReferrals: 0,
        totalReferrals: 0,
    });
    const [communityMembers, setCommunityMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("staking"); // "community", "pack", "staking"
    const [packData, setPackData] = useState([]);
    const [stakingData, setStakingData] = useState([]);
    const [isLoadingPack, setIsLoadingPack] = useState(false);
    const [isLoadingStaking, setIsLoadingStaking] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [GroupStakingData, setGroupStakingData] = useState(0);
    const [smallZoneData, setSmallZoneData] = useState(0);
    const [isLoadingTotalGroupStaking, setIsLoadingTotalGroupStaking] = useState(false);
    const itemsPerPage = 10;
    const [viewingAddress, setViewingAddress] = useState(null); // null means viewing current user
    const [staking_ranking, setStaking_ranking] = useState(0);
    const [community_ranking, setCommunity_ranking] = useState(0);
    const [generation_rank, setGenerationRank] = useState(0);
    // Ref to access latest communityMembers without causing dependency issues
    const communityMembersRef = useRef(communityMembers);

    // Update ref whenever communityMembers changes
    useEffect(() => {
        communityMembersRef.current = communityMembers;
    }, [communityMembers]);

    // Reset pagination when data changes or when switching addresses
    useEffect(() => {
        setCurrentPage(1);
    }, [communityMembers.length, stakingData.length, viewingAddress]);
    // Stack of addresses we've drilled into: [A, B] = My → A → B. Back pops one level.
    const [viewStack, setViewStack] = useState([]);
    const [, setViewingMemberInfo] = useState(null); // Store info about viewed member (setter used in viewMemberCommunity)
    const [searchAddress, setSearchAddress] = useState(""); // Search input state
    // Rank from Bonus contract (uint8 0–10) for current user; null when not loaded
    const [userRankFromContract, setUserRankFromContract] = useState(null);
    // Direct referral count from Referral contract (getReferredUserCount); null when not loaded
    const [directCountFromContract, setDirectCountFromContract] = useState(null);
    // Total tree (community) count from Referral contract (getTotalTreeCount); null when not loaded
    const [communityCountFromContract, setCommunityCountFromContract] = useState(null);
    // Total bonus received from Bonus.totalBonusReceived(address); null when not loaded
    const [totalBonusFromContract, setTotalBonusFromContract] = useState(null);
    const [totalStakingBonusFromContract, setTotalStakingBonusFromContract] = useState(null);

    // Fetch user profile – uses account; level/directCount from contracts
    const fetchUserProfile = useCallback(async () => {
        if (!account) {
            setUserProfile({
                id: "",
                level: "S0",
                address: "",
                totalBonus: "0",
                teamInvestment: "0",
                directReferrals: 0,
                totalReferrals: 0,
            });
            return;
        }
        setUserProfile({
            id: `${account.slice(0, 6)}...${account.slice(-4)}`,
            level: "S0", // Updated from Bonus.getUserRank
            address: account,
            totalBonus: "0",
            teamInvestment: "0",
            directReferrals: 0, // Updated from Referral.getReferredUserCount
            totalReferrals: 0,
        });
    }, [account]);

    // Fetch current user's rank from Bonus contract (getUserRank(user, monthIndex))
    // Use getCurrentMonthIndex() so "my" rank matches downline logic (same month index).
    const fetchUserRank = useCallback(async (targetAddressOverride = null) => {
        const targetAddress = targetAddressOverride || account;
        if (!account || !executeWithFailover) return;
        try {
            const rank = await executeWithFailover(async (provider) => {
                const bonusContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.Bonus,
                    CONTRACT_ABIS.BONUS,
                    provider
                );
                const monthIdx = await getCachedMonthIndex(bonusContract);
                const r = await bonusContract.getUserRank(targetAddress, monthIdx);
                if (r == null) return null;
                // Handle BigNumber (ethers v5) or bigint (ethers v6)
                const num = typeof r === "bigint"
                    ? Number(r)
                    : (r && typeof r.toNumber === "function"
                        ? r.toNumber()
                        : Number(r));
                return Number.isNaN(num) ? null : num;
            });
            setUserRankFromContract(rank);
        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA" && !e?.message?.includes("Wallet not detected")) {
                console.warn("Bonus.getUserRank failed:", e);
            }
            setUserRankFromContract(null);
        }
    }, [account, executeWithFailover]);

    // Fetch direct referral count from Referral contract (getReferredUserCount(address))
    const fetchDirectCount = useCallback(async (addressOverride = null) => {
        const targetAddress = addressOverride ?? account;
        if (!targetAddress || !executeWithFailover) return;
        try {
            const count = await executeWithFailover(async (provider) => {
                const referralContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.REFERRALS,
                    CONTRACT_ABIS.REFERRALS,
                    provider
                );
                const c = await referralContract.getReferredUserCount(targetAddress);
                if (c == null) return null;
                const num = typeof c === "bigint"
                    ? Number(c)
                    : (c && typeof c.toNumber === "function"
                        ? c.toNumber()
                        : Number(c));
                return Number.isNaN(num) ? null : num;
            });
            setDirectCountFromContract(count);
        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA" && !e?.message?.includes("Wallet not detected")) {
                console.warn("Referral.getReferredUserCount failed:", e);
            }
        }
    }, [account, executeWithFailover]);

    // Fetch total tree (community) count from Referral contract (getTotalTreeCount(address))
    const fetchCommunityCount = useCallback(async (addressOverride = null) => {
        const targetAddress = addressOverride ?? account;
        if (!targetAddress || !executeWithFailover) return;
        try {
            const count = await executeWithFailover(async (provider) => {
                const referralContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.REFERRALS,
                    CONTRACT_ABIS.REFERRALS,
                    provider
                );
                const c = await referralContract.getTotalTreeCount(targetAddress);
                if (c == null) return null;
                const num = typeof c === "bigint"
                    ? Number(c)
                    : (c && typeof c.toNumber === "function"
                        ? c.toNumber()
                        : Number(c));
                return Number.isNaN(num) ? null : num;
            });
            setCommunityCountFromContract(count);
        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA" && !e?.message?.includes("Wallet not detected")) {
                console.warn("Referral.getTotalTreeCount failed:", e);
            }
        }
    }, [account, executeWithFailover]);

    // Fetch total bonus from Bonus contract: sum amounts from getAllBonusRecords(user)
    const fetchTotalBonus = useCallback(async (addressOverride = null) => {
        const targetAddress = addressOverride ?? account;
        if (!targetAddress || !executeWithFailover) return;
        try {
            const total = await executeWithFailover(async (provider) => {
                const bonusContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.Bonus,
                    CONTRACT_ABIS.BONUS,
                    provider
                );
                const records = await bonusContract.getAllBonusRecords(targetAddress);
                if (!records || !Array.isArray(records) || records.length === 0) {
                    return "0";
                }
                let sum = 0n;
                for (const r of records) {
                    const amt = r?.amount ?? r?.[1] ?? r;
                    // eslint-disable-next-line no-undef
                    sum += typeof amt === "bigint" ? amt : BigInt(amt?.toString?.() ?? "0");
                }
                return sum.toString();
            });
            setTotalBonusFromContract(total);
        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA") {
                console.warn("Bonus.getAllBonusRecords failed:", e);
            }
        }
    }, [account, executeWithFailover]);

    // Fetch data from the public API using native fetch
    // const fetchAPIData = useCallback(async (address) => {
    //     // Use environment variable for API base URL (must start with REACT_APP_ in React)
    //     const apiBaseUrl =
    //         process.env.REACT_APP_COMMUNITY_API_URL ||
    //         "https://public-buynode.nextfi.now";
    //     const url = `${apiBaseUrl}/publicApi/getReferredUsers/?address=${address}`;
    //     const timeoutMs = 15000; // 15s
    //     const controller = new AbortController();
    //     const id = setTimeout(() => controller.abort(), timeoutMs);

    //     try {
    //         const response = await fetch(url, {
    //             method: "GET",
    //             headers: {
    //                 Accept: "application/json",
    //             },
    //             signal: controller.signal,
    //         });

    //         clearTimeout(id);

    //         // Check if response is ok first
    //         if (!response.ok) {
    //             // Don't show error toast for 404s or other expected errors
    //             if (response.status >= 500) {
    //                 toast.error(t("validation.failedToLoadNodesData"));
    //             } else if (response.status === 404) {
    //                 // console.log('API endpoint returned 404');
    //             } else {
    //                 toast.error(t("validation.failedToLoadNodesData"));
    //             }
    //             return null;
    //         }

    //         // Try to parse JSON
    //         const data = await response.json();
    //         // Transform the data to match expected format if it's just an array of addresses
    //         if (Array.isArray(data) && data.length > 0) {
    //             // If API returns array like ["0xf257137dc486a291806f32847df08c8356c36043", "0xabc123..."]
    //             const transformedData = {
    //                 children: data.map((address, index) => {
    //                     // For address-only arrays, we'll set default values
    //                     // Investment amounts can be fetched later via blockchain if needed
    //                     return {
    //                         address: address,
    //                     };
    //                 }),
    //             };
    //             toast.success(t("common.success"));
    //             return transformedData;
    //         }

    //         // If API already returns the expected format with children array
    //         if (data && data.children && Array.isArray(data.children)) {
    //             // Add level to existing children if not present
    //             const transformedData = {
    //                 children: data.children.map((child, index) => ({
    //                     ...child,
    //                 })),
    //             };
    //             toast.success(t("common.success"));
    //             return transformedData;
    //         }

    //         // If API returns object but no children property, try to create children from addresses
    //         if (data && typeof data === "object") {
    //             // Look for any array property that might contain addresses
    //             const possibleArrayProps = Object.keys(data).find((key) =>
    //                 Array.isArray(data[key])
    //             );
    //             if (possibleArrayProps && data[possibleArrayProps].length > 0) {
    //                 const transformedData = {
    //                     children: data[possibleArrayProps].map(
    //                         (item, index) => {
    //                             const level = `S${(index % 9) + 1}`; // Example level assignment

    //                             return {
    //                                 address:
    //                                     typeof item === "string"
    //                                         ? item
    //                                         : item.address ||
    //                                         `0x${"0".repeat(40)}`,
    //                             };
    //                         }
    //                     ),
    //                 };
    //                 toast.success(t("common.success"));
    //                 return transformedData;
    //             }
    //         }

    //         toast.success(t("common.success"));
    //         return data;
    //     } catch (error) {
    //         clearTimeout(id);
    //         // console.error('❌ Error in fetchAPIData:', error);

    //         // Handle specific error types
    //         if (error.name === "AbortError") {
    //             toast.error(t("validation.failedToLoadNodesData"));
    //             return null;
    //         }

    //         if (
    //             error.name === "TypeError" &&
    //             error.message.includes("Failed to fetch")
    //         ) {
    //             // console.log('Network error - could not reach API server');
    //         }

    //         if (error.message.includes("JSON")) {
    //             toast.error(t("validation.failedToLoadNodesData"));
    //             return null;
    //         }

    //         // Other errors
    //         toast.error(t("validation.failedToLoadNodesData"));
    //         return null;
    //     }
    // }, []);

    const fetchTotalGroupStaking = useCallback(async (targetAddressOverride = null) => {
        const targetAddress = targetAddressOverride || account;
        if (!targetAddress || !isCorrectNetwork) {
            setGroupStakingData(0);
            setSmallZoneData(0);
            return;
        }

        try {
            const { groupStaking, smallZoneStaking, totalStake } = await executeWithFailover(async (provider) => {
                const stakingContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.STAKING,
                    CONTRACT_ABIS.STAKING,
                    provider
                );
                const smallZoneStaking = await stakingContract.getSmallZoneSales(targetAddress);
                const groupStaking = await stakingContract.teamTotalInvestValue(targetAddress);
                const totalStake = await stakingContract.userStakeAmount(targetAddress);
                return { groupStaking: ethers.formatUnits(groupStaking, 18), smallZoneStaking: ethers.formatUnits(smallZoneStaking, 18), totalStake: ethers.formatUnits(totalStake, 18) };
            });
            // const totalGroupSales = totalStake + groupStaking
            setGroupStakingData(groupStaking);
            setSmallZoneData(smallZoneStaking);
        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA" && !e?.message?.includes("Wallet not detected")) {
                console.warn("fetchTotalGroupStaking failed:", e);
            }
            setGroupStakingData(0);
            setSmallZoneData(0);
            // toast.error(t("validation.failedToLoadNodesData"));
            console.log("fetchTotalGroupStaking failed:", e);
            return;
        }
    }, [executeWithFailover, isCorrectNetwork, account]);


    // Format Bonus uint256 amounts for display (18 decimals)
    const formatBonusAmount = useCallback((val) => {
        if (val == null) return "0.00";
        const raw = typeof val === "bigint" ? val.toString() : (val?.toString ? val.toString() : String(val));
        const parsed = parseFloat(raw);
        if (Number.isNaN(parsed)) return "0.00";
        return (parsed / 1e18).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }, []);

    // Fetch downline from Referral contract; sales = personal + group from Bonus
    const fetchCommunityMembers = useCallback(async (targetAddressOverride = null, searchedAddress = null) => {
        const targetAddress = targetAddressOverride || account;

        if (!targetAddress || !executeWithFailover) {
            setCommunityMembers([]);
            return [];
        }

        setIsLoading(true);
        try {
            // Step 1: get addresses + monthIdx in a single RPC session
            const { addresses, monthIdx } = await executeWithFailover(async (provider) => {
                const referralContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.REFERRALS,
                    CONTRACT_ABIS.REFERRALS,
                    provider
                );
                const bonusContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.Bonus,
                    CONTRACT_ABIS.BONUS,
                    provider
                );
                const count = await referralContract.getReferredUserCount(targetAddress);
                const n = typeof count === "bigint" ? Number(count) : (count?.toNumber?.() ?? Number(count)) || 0;
                const ZERO = "0x0000000000000000000000000000000000000000";
                // Batch referredUsers calls (parallel within one provider session)
                const addrPromises = [];
                for (let i = 0; i < n; i++) {
                    addrPromises.push(referralContract.referredUsers(targetAddress, i));
                }
                const rawAddrs = await Promise.all(addrPromises);
                const addrs = rawAddrs
                    .map((a) => (a && typeof a === "string" ? a : (a?.toString?.() || String(a))))
                    .filter((a) => a && a.toLowerCase() !== ZERO.toLowerCase());
                const monthIdx = await getCachedMonthIndex(bonusContract);
                return { addresses: addrs, monthIdx };
            });

            // Step 2: fetch each member's details with concurrency limit (max 3 parallel)
            const memberTasks = (addresses || []).map((addr, index) => async () => {
                let personalSales = 0n;
                let groupSalesVal = 0n;
                let rank = 0;
                try {
                    const [ps, gs, r] = await executeWithFailover(async (provider) => {
                        const bonusContract = new ethers.Contract(
                            CONTRACT_ADDRESSES.Bonus,
                            CONTRACT_ABIS.BONUS,
                            provider
                        );
                        const [p, g, rankRes] = await Promise.all([
                            bonusContract.getPersonalSales(addr, monthIdx),
                            bonusContract.getGroupSales(addr, monthIdx),
                            bonusContract.getUserRank(addr, monthIdx).catch(() => 0),
                        ]);
                        return [p, g, rankRes];
                    });
                    personalSales = ps ?? 0n;
                    groupSalesVal = gs ?? 0n;
                    rank = typeof r === "bigint" ? Number(r) : (r?.toNumber?.() ?? Number(r)) ?? 0;
                } catch (_) { }
                const personalStr = formatBonusAmount(personalSales);
                const groupStr = formatBonusAmount(groupSalesVal);
                const sum = parseFloat(personalStr.replace(/,/g, "")) + parseFloat(groupStr.replace(/,/g, ""));
                const salesDisplay = sum.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return {
                    id: `${addr}-${index}`,
                    address: addr,
                    level: `S${Math.min(10, Math.max(0, rank))}`,
                    amount: salesDisplay,
                    totalStake: "0",
                };
            });
            const transformedMembers = await batchAsync(memberTasks, 3);

            if (searchedAddress) {
                const filteredMembers = transformedMembers.filter((m) => m.address.toLowerCase() === searchedAddress.toLowerCase());
                communityMembersRef.current = filteredMembers;
                setCommunityMembers(filteredMembers);
                setGroupSales((prev) => ({ ...prev, totalMembers: filteredMembers.length }));
                return filteredMembers;
            } else {
                // Update ref immediately so callers can use it synchronously
                // (e.g. viewMyOwnCommunity calls fetchStakingData right after await fetchCommunityMembers).
                communityMembersRef.current = transformedMembers;
                setCommunityMembers(transformedMembers);
                setGroupSales((prev) => ({ ...prev, totalMembers: transformedMembers.length }));
                return transformedMembers;
            }

        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA" && !e?.message?.includes("Wallet not detected")) {
                console.warn("fetchCommunityMembers failed:", e);
            }
            setCommunityMembers([]);
            communityMembersRef.current = [];
            setGroupSales((prev) => ({ ...prev, totalMembers: 0 }));
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [account, executeWithFailover, formatBonusAmount]);

    // Fetch pack sales: getPersonalSales, getGroupSales from Bonus; level, count from contracts
    const fetchPackSales = useCallback(
        async (targetAddressOverride = null) => {
            const targetAddress =
                targetAddressOverride || account;

            if (!targetAddress || !executeWithFailover) {
                setGroupSales({
                    level: "S0",
                    amount: "0",
                    address: targetAddress || "",
                    totalStake: "0",
                    totalMembers: 0,
                    totalVolume: "0",
                    smallZoneVolume: "0",
                    activeMembers: 0,
                });
                return;
            }

            setIsLoadingGroupSales(true);
            try {
                const [count, rank, personalSales, groupSalesVal] = await executeWithFailover(async (provider) => {
                    const referralContract = new ethers.Contract(
                        CONTRACT_ADDRESSES.REFERRALS,
                        CONTRACT_ABIS.REFERRALS,
                        provider
                    );
                    const bonusContract = new ethers.Contract(
                        CONTRACT_ADDRESSES.Bonus,
                        CONTRACT_ABIS.BONUS,
                        provider
                    );
                    const monthIdx = await getCachedMonthIndex(bonusContract);
                    const [c, r, ps, gs] = await Promise.all([
                        referralContract.getReferredUserCount(targetAddress),
                        bonusContract.getUserRank(targetAddress, monthIdx).catch(() => 0),
                        bonusContract.getPersonalSales(targetAddress, monthIdx).catch(() => null),
                        bonusContract.getGroupSales(targetAddress, monthIdx).catch(() => null),
                    ]);
                    const countNum = typeof c === "bigint" ? Number(c) : (c?.toNumber?.() ?? Number(c));
                    const rankNum = typeof r === "bigint" ? Number(r) : (r?.toNumber?.() ?? Number(r));
                    return [
                        Number.isNaN(countNum) ? 0 : countNum,
                        Number.isNaN(rankNum) ? 0 : Math.min(10, Math.max(0, rankNum)),
                        ps,
                        gs,
                    ];
                });

                const personalFormatted = formatBonusAmount(personalSales);
                const groupFormatted = formatBonusAmount(groupSalesVal);

                setGroupSales({
                    level: `S${rank}`,
                    amount: personalFormatted,
                    address: targetAddress,
                    totalStake: "0",
                    totalMembers: count || 0,
                    totalVolume: groupFormatted,
                    smallZoneVolume: "0",
                    activeMembers: count || 0,
                });
            } catch (e) {
                if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA" && !e?.message?.includes("Wallet not detected")) {
                    console.warn("fetchPackSales failed:", e);
                }
                // Keep previous groupSales state — don't reset to 0.
                // Only update the address so the UI stays consistent.
                setGroupSales((prev) => ({ ...prev, address: targetAddress }));
            } finally {
                setIsLoadingGroupSales(false);
            }
        },
        [account, executeWithFailover, formatBonusAmount]
    );

    // Fetch staking data
    const fetchStakingData = useCallback(async (targetAddressOverride = null) => {
        const targetAddress = targetAddressOverride || account;
        if (!targetAddress || !executeWithFailover || !isCorrectNetwork || !isConnected) {
            setStakingData([]);
            return;
        }

        setIsLoadingStaking(true);
        try {
            // Use ref to get latest communityMembers without adding it to dependencies
            const members = communityMembersRef.current;

            if (members.length > 0) {
                // Use Promise.all with map instead of forEach to properly await all API calls
                const records = await Promise.all(
                    members.map(async (member) => {
                        try {
                            const { totalStake, weeklyRank } = await executeWithFailover(async (provider) => {
                                const stakingContract = new ethers.Contract(
                                    CONTRACT_ADDRESSES.STAKING,
                                    CONTRACT_ABIS.STAKING,
                                    provider
                                );

                                const totalStake = await stakingContract.userStakeAmount(member.address);
                                // const groupStaking = await stakingContract.teamTotalInvestValue(member.address);
                                const weeklyRank = await stakingContract.userCommunityRank(member.address);
                                return { totalStake: ethers.formatUnits(totalStake, 18), weeklyRank: Number(weeklyRank) };
                            });

                            return {
                                id: member.id || member.address,
                                address: member.address,
                                totalStake: totalStake,
                                level: `V${weeklyRank}`
                            };
                        } catch (error) {
                            console.warn(`Failed to fetch staking for ${member.address}:`, error);
                            return null;
                        }
                    })
                );

                // Filter out null results
                const validRecords = records.filter(record => record !== null);
                setStakingData(validRecords);
            } else {
                setStakingData([]);
            }
        } catch (e) {
            if (e?.code !== "CALL_EXCEPTION" && e?.code !== "BAD_DATA" && !e?.message?.includes("Wallet not detected")) {
                console.warn("fetchStakingData failed:", e);
            }
            setStakingData([]);
        } finally {
            setIsLoadingStaking(false);
        }
    }, [account, executeWithFailover, isCorrectNetwork, isConnected, communityMembersRef]);

    const viewMemberCommunity = async (memberAddress, memberLevel) => {
        setViewStack((prev) => [...prev, memberAddress]);
        setViewingAddress(memberAddress);
        setViewingMemberInfo({
            address: memberAddress,
            level: memberLevel,
        });
        // Clear current data and reload with new address
        setCommunityMembers([]);
        setPackData([]);
        setStakingData([]);
        setIsLoading(true);

        // Trigger data refresh for the selected member
        // Pass address directly to avoid race condition
        try {
            // Dependency: `fetchStakingData` reads from `communityMembersRef.current`,
            // so we must load community members first (otherwise first click shows empty staking).
            await fetchCommunityMembers(memberAddress);

            const tasks = [
                fetchPackSales(memberAddress),
                fetchTotalGroupStaking(memberAddress),
                fetchStakingData(memberAddress),
                getRanking(memberAddress),
                fetchTotalStakingBonus(memberAddress),
                fetchUserRank(memberAddress),
            ];

            // Fetch stats for the member in parallel (non-blocking; partial failure OK)
            if (executeWithFailover) {
                tasks.push(
                    fetchDirectCount(memberAddress),
                    fetchCommunityCount(memberAddress),
                    fetchTotalBonus(memberAddress)
                );
            }

            await Promise.allSettled(tasks);
        } catch (error) {
            console.error("❌ Error fetching member data:", error);
            // toast.error(t("validation.failedToLoadNodesData"));
        } finally {
            setIsLoading(false);
        }
    };

    // Navigate back to current user's view
    const viewMyOwnCommunity = async () => {
        if (!account) return;
        isReturningToMyRef.current = true;
        setViewStack([]);
        setViewingAddress(null);
        setViewingMemberInfo(null);
        setSearchAddress(""); // Clear search field when returning to own community
        setCommunityMembers([]);
        setPackData([]);
        setStakingData([]);
        // Don't reset groupSales / bonus counts to 0 here – fetch first, then update.
        // Otherwise UI shows 0.00 if fetch is slow or fails.
        setIsLoading(true);

        try {
            await fetchCommunityMembers(account);
            await fetchStakingData(account);
            // Use allSettled so one failing RPC (e.g. BAD_DATA/CALL_EXCEPTION) doesn't lose other data.
            // Do NOT gate by executeWithFailover here; each fetch already guards internally.
            const results = await Promise.allSettled([
                fetchPackSales(account),
                fetchUserRank(account),
                fetchDirectCount(account),
                fetchCommunityCount(account),
                fetchTotalBonus(account),
                fetchTotalStakingBonus(account),
                fetchTotalGroupStaking(account),
                getRanking(account),
            ]);

            const failed = results.filter((r) => r.status === "rejected");
            if (failed.length > 0 && failed.length === results.length) {
                // toast.error(t("validation.failedToLoadNodesData"));
            }
        } catch (error) {
            console.error("❌ Error fetching own community data:", error);
            // toast.error(t("validation.failedToLoadNodesData"));
        } finally {
            isReturningToMyRef.current = false;
            setIsLoading(false);
        }
    };

    // Check if user has permission to view the searched address (MOCKED: Always allow for fake data)
    const checkViewPermission = async (searchedAddress) => {
        // Always return true for fake data demo
        return true;
    };

    // Search for address downline records
    const searchAddressDownline = useCallback(async (address) => {
        // Validate address format (basic check)
        if (!address || address.length < 10) {
            toast.error(t("community.invalidWalletAddress"));
            return;
        }

        // Check if user is connected
        if (!account || !isConnected) {
            toast.error(t("community.connectWalletFirst"));
            return;
        }

        // Clean the address (remove spaces and ensure it starts with 0x)
        let cleanAddress = address.trim();
        if (!cleanAddress.startsWith("0x")) {
            cleanAddress = "0x" + cleanAddress;
        }

        // Basic validation for Ethereum address format
        if (cleanAddress.length !== 42) {
            toast.error(t("community.invalidAddressFormat"));
            return;
        }

        // Show loading state while checking permission
        setIsLoading(true);
        toast.loading(t("community.checkingPermissions"), {
            id: "permission-check",
        });

        try {
            // Check permission to view this address
            const hasPermission = await checkViewPermission(cleanAddress);

            if (!hasPermission) {
                toast.dismiss("permission-check");
                toast.error(t("community.permissionDenied"));
                setIsLoading(false);
                return;
            }

            toast.dismiss("permission-check");
            const addressDisplay = `${cleanAddress.slice(
                0,
                6
            )}...${cleanAddress.slice(-4)}`;
            toast.success(
                t("community.permissionGranted").replace(
                    "{address}",
                    addressDisplay
                )
            );

            // Set the viewing address and info immediately (push to stack so back works)
            setViewStack((prev) => [...prev, cleanAddress]);
            setViewingAddress(cleanAddress);
            setViewingMemberInfo({
                address: cleanAddress,
                level: "Unknown", // We'll determine level when data loads
            });

            // Trigger data refresh for the searched address immediately
            // Pass the address directly to avoid race condition with state updates
            try {
                await fetchCommunityMembers(null, cleanAddress);
            } catch (dataError) {
                console.error(
                    "Error fetching data for searched address:",
                    dataError
                );
                // toast.error(t("validation.failedToLoadNodesData"));
                setIsLoading(false);
            }

            // Keep search input visible - don't clear it
            // setSearchAddress(""); // Removed to keep input visible
        } catch (error) {
            console.error("Search error:", error);
            toast.dismiss("permission-check");
            toast.error(t("community.permissionCheckFailed"));
            setIsLoading(false);
        }
    }, [account, isConnected, executeWithFailover, t, fetchCommunityMembers]);

    // Handle search input submission
    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (searchAddress.trim()) {
            await searchAddressDownline(searchAddress);
        }
    };

    // Handle search input change (no auto-search)
    const handleSearchInputChange = async (e) => {
        const value = e.target.value;
        setSearchAddress(value);

        // If input is empty, return to user's own community
        if (!value.trim()) {
            if (viewingAddress) {
                await viewMyOwnCommunity();
            }
        }
    };

    // Copy address to clipboard
    const copyAddress = (address) => {
        navigator.clipboard.writeText(address).then(
            () => {
                toast.success(t("community.addressCopied"));
            },
            () => {
                toast.error(t("community.copyFailed"));
            }
        );
    };

    // Truncate address for display
    const truncateAddress = (address) => {
        if (!address) return "";
        if (address.length <= 20) return address;
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    // Get adjacent ranks based on current rank
    const getAdjacentRanks = (currentRank) => {
        // Add null/undefined check and default to "S0" if invalid
        if (
            !currentRank ||
            typeof currentRank !== "string" ||
            !currentRank.startsWith("S")
        ) {
            currentRank = "S0";
        }

        const rankNumber = parseInt(currentRank.substring(1)) || 0;

        // Calculate adjacent ranks in circular manner
        let higherRank1, higherRank2, lowerRank1, lowerRank2;

        if (rankNumber === 0) {
            // S0 (lowest): higher S8,S9, current S0, lower S1,S2
            higherRank2 = 8;
            higherRank1 = 9;
            lowerRank1 = 1;
            lowerRank2 = 2;
        } else if (rankNumber === 1) {
            higherRank2 = 9;
            higherRank1 = 0;
            lowerRank1 = 2;
            lowerRank2 = 3;
        } else if (rankNumber === 2) {
            higherRank2 = 0;
            higherRank1 = 1;
            lowerRank1 = 3;
            lowerRank2 = 4;
        } else if (rankNumber === 9) {
            // S9: higher S7,S8, current S9, lower S10,S0
            higherRank2 = 7;
            higherRank1 = 8;
            lowerRank1 = 10;
            lowerRank2 = 0;
        } else if (rankNumber === 10) {
            // S10 (highest): higher S8,S9, current S10, lower S0,S1
            higherRank2 = 8;
            higherRank1 = 9;
            lowerRank1 = 0;
            lowerRank2 = 1;
        } else {
            // S3–S8: +/- 1 and 2, clamp to 0–10
            higherRank2 = Math.max(rankNumber - 2, 0);
            higherRank1 = Math.max(rankNumber - 1, 0);
            lowerRank1 = Math.min(rankNumber + 1, 10);
            lowerRank2 = Math.min(rankNumber + 2, 10);
        }

        return {
            left1: `S${higherRank2}`, // Higher rank 2
            left2: `S${higherRank1}`, // Higher rank 1
            right1: `S${lowerRank1}`, // Lower rank 1
            right2: `S${lowerRank2}`, // Lower rank 2
        };
    };

    const fetchTotalStakingBonus = useCallback(async (targetAddressOverride = null) => {
        const targetAddress = targetAddressOverride || account;
        if (!targetAddress || !isCorrectNetwork) {
            setTotalStakingBonusFromContract(0);
            return;
        }

        try {
            const { maxCap, remainingMaxCap } = await executeWithFailover(async (provider) => {
                const stakingContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.STAKING,
                    CONTRACT_ABIS.STAKING,
                    provider
                );

                const { maxCap, remainingMaxCap } = await stakingContract.getUserROIAndCapLive(targetAddress);
                return { maxCap: maxCap, remainingMaxCap: remainingMaxCap };
            });
            const totalMaxCap = formatBonusAmount(remainingMaxCap) + "/" + formatBonusAmount(maxCap);
            setTotalStakingBonusFromContract(totalMaxCap);
        } catch (error) {
            console.error("Error fetching total staking bonus:", error);
            // toast.error(t("validation.failedToLoadNodesData"));
        }

    }, [account, isCorrectNetwork, executeWithFailover]);

    // Load data when component mounts or account changes
    useEffect(() => {
        if (account && isConnected) {
            // Delay to allow wallet provider (window.ethereum) and RPC to be ready on reload
            const timer = setTimeout(async () => {
                fetchUserProfile();
                fetchPackSales();
                await fetchCommunityMembers();
                fetchUserRank();
                fetchTotalBonus();
                fetchDirectCount();
                fetchCommunityCount();
                fetchStakingData();
                fetchTotalGroupStaking();
                fetchTotalStakingBonus();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [
        account,
        isConnected,
        fetchUserProfile,
        fetchCommunityMembers,
        fetchPackSales,
        fetchUserRank,
        fetchTotalBonus,
        fetchDirectCount,
        fetchCommunityCount,
        fetchStakingData,
        fetchTotalStakingBonus,
    ]);

    // Removed: the old viewingAddress-change effect that re-fetched directCount, communityCount, totalBonus.
    // These are already fetched inside viewMyOwnCommunity / viewMemberCommunity / initial load,
    // so the extra effect was causing duplicate RPC calls every time we drilled into or out of a member.

    // Keep function references updated
    useEffect(() => {
        fetchFunctionsRef.current = {
            fetchUserProfile,
            fetchCommunityMembers,
            fetchPackSales,
        };
    });

    // Only reset when user disconnects (!account). Do not reset when account is set,
    // or we overwrite data when returning from downline view or on initial load.
    useEffect(() => {
        if (!account) {
            setGroupSales({
                level: "S0",
                amount: "0",
                address: "",
                totalStake: "0",
                totalMembers: 0,
                totalVolume: "0",
                activeMembers: 0,
            });
            setIsLoadingGroupSales(false);
            setUserRankFromContract(null);
            setDirectCountFromContract(null);
            setCommunityCountFromContract(null);
            setTotalBonusFromContract(null);
            setGroupStakingData(0);
            setSmallZoneData(0);
            setTotalStakingBonusFromContract(0);
        }
    }, [account]);

    // Mock data for rewards - replace with actual data
    const rewardData = {
        directCount: 0,
        communityCount: 0,
    };

    // Get tier badge text (P0–P10) from level (S0–S10)
    const getTierBadge = (level) => {
        if (!level) return "P0";
        const num = parseInt(level.replace("S", ""), 10);
        if (Number.isNaN(num)) return "P0";
        return `P${Math.min(10, Math.max(0, num))}`;
    };

    const formatGenerationRank = useCallback((rankValue) => {
        const rankNumber = Number(rankValue);
        const safeRank = Number.isFinite(rankNumber) ? Math.max(0, Math.min(10, rankNumber)) : 0;

        if (safeRank === 0) {
            return language?.startsWith("zh") ? "0代" : "Tier 0";
        }

        const startTier = safeRank * 2 - 1;
        const endTier = safeRank * 2;
        return language?.startsWith("zh")
            ? `${startTier}&${endTier}代`
            : `Tier ${startTier}&${endTier}`;
    }, [language]);

    const getRanking = useCallback(async (targetAddressOverride = null) => {
        const targetAddress = targetAddressOverride || account;
        if (!targetAddress || !executeWithFailover || !isCorrectNetwork || !isConnected) return;
        const rankingResult = await executeWithFailover(async (provider) => {
            const stakingContract = new ethers.Contract(
                CONTRACT_ADDRESSES.STAKING,
                CONTRACT_ABIS.STAKING,
                provider
            );
            const stakingRank = await stakingContract.userActiveRank(targetAddress);
            const communityRank = await stakingContract.userCommunityRank(targetAddress);
            const generationRank = await stakingContract.userGenerationRank(targetAddress);
            return {
                staking_ranking: Number(stakingRank),
                community_ranking: Number(communityRank),
                generation_rank: Number(generationRank),
            };
        });

        setStaking_ranking(`${rankingResult.staking_ranking}%`);
        setCommunity_ranking(`V${rankingResult.community_ranking}`);
        setGenerationRank(formatGenerationRank(rankingResult.generation_rank));
    }, [account, executeWithFailover, isCorrectNetwork, isConnected, formatGenerationRank]);

    useEffect(() => {
        getRanking();
    }, [getRanking]);

    // When viewing own profile, prefer rank from Bonus contract (0–10); null = use groupSales.level
    const displayedLevel = (!viewingAddress && userRankFromContract != null)
        ? `S${Math.min(10, Math.max(0, userRankFromContract))}`
        : groupSales.level;

    return (
        <div className="relative overflow-x-hidden min-h-[calc(100vh-200px)]">
            {/* Loading modal – shown until all loading finishes */}
            {isLoading && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 backdrop-blur-sm"
                    aria-live="polite"
                    aria-busy="true"
                >
                    <div className="flex flex-col items-center gap-4 rounded-2xl bg-[#1a1023]/95 border border-[#9770DD]/30 px-8 py-6 shadow-xl">
                        <div className="size-12 animate-spin rounded-full border-4 border-[#9770DD]/30 border-t-[#9770DD]" />
                        <p className="text-white font-medium">{t("community.loadingCommunityData")}</p>
                    </div>
                </div>
            )}

            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10"></div>

            {/* Subtle geometric pattern overlay */}
            <div
                className="absolute inset-0 opacity-[0.03] pointer-events-none -z-10"
                style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #9770DD 1px, transparent 0)`,
                    backgroundSize: '40px 40px'
                }}
            ></div>

            {/* Additional pattern overlay */}
            <div
                className="absolute inset-0 opacity-[0.02] pointer-events-none -z-10"
                style={{
                    backgroundImage: `linear-gradient(45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%), linear-gradient(-45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%)`,
                    backgroundSize: '60px 60px'
                }}
            ></div>

            {/* ------ New Ui Start here ------- */}

            <div className="relative max-w-[550px] mx-auto mb-6">
                {/* Header with back button */}
                <div className="flex items-center justify-between gap-4 px-4 relative z-20">
                    <button
                        onClick={async () => {
                            if (viewStack.length === 0) {
                                navigate(-1);
                                return;
                            }
                            if (viewStack.length === 1) {
                                await viewMyOwnCommunity();
                                return;
                            }
                            const newStack = viewStack.slice(0, -1);
                            const prevAddress = newStack[newStack.length - 1];
                            setViewStack(newStack);
                            setViewingAddress(prevAddress);
                            setViewingMemberInfo({ address: prevAddress, level: "" });
                            setCommunityMembers([]);
                            setIsLoading(true);
                            try {
                                await fetchCommunityMembers(prevAddress);
                                await fetchPackSales(prevAddress);
                                if (executeWithFailover) {
                                    await Promise.allSettled([
                                        fetchDirectCount(prevAddress),
                                        fetchCommunityCount(prevAddress),
                                        fetchTotalBonus(prevAddress),
                                    ]);
                                }
                            } catch (err) {
                                console.error("Error loading previous level:", err);
                                // toast.error(t("validation.failedToLoadNodesData"));
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                        className="material-symbols-outlined text-white text-2xl hover:text-[#9770DD] transition-colors"
                    >
                        arrow_back
                    </button>


                    {/* Search Input */}
                    <div className="flex items-center bg-[#05000a] border border-[#231735] rounded-xl px-4 py-2.5 mb-6 mt-4 w-full min-w-0">
                        <FiSearch className="text-white mr-3" size={14} strokeWidth={2.5} />
                        <input
                            type="text"
                            placeholder={t("Enter wallet addrres to search") || "Enter wallet addrres to search"}
                            className="flex-1 bg-transparent text-[12px] text-white focus:outline-none placeholder-gray-500 min-w-0"
                        />
                        <button className="bg-transparent border border-[#231735] text-white text-[14px] px-3 py-1.5 rounded-lg ml-2 whitespace-nowrap hover:bg-[#1a1226] transition-colors">
                            {t("community.search") || "community.search"}
                        </button>
                    </div>
                </div>

                {/* Top Section with Profile & Curve */}
                <div className="relative w-full flex flex-col items-center mt-2 px-4"
                    style={{ backgroundImage: `url(${background_tree_img})`, backgroundSize: "100%", backgroundPosition: "center top", backgroundRepeat: "no-repeat" }}
                >
                    {/* The curved line with nodes */}
                    {/* <div className="relative w-full h-[100px] flex items-center justify-center mb-4">
                        <img src={CurveLineImg} alt="Curve" className="absolute top-[35%] left-1/2 -translate-x-1/2 -translate-y-[35%] w-[120%] max-w-[400px] object-contain opacity-60 pointer-events-none rotate-180" />

                        <div className="relative w-full max-w-[340px] flex justify-center items-center z-10 px-4 gap-8">
                            {(() => {
                                const adjacent = getAdjacentRanks(displayedLevel);
                                const nodes = [
                                    { rank: adjacent.left2, isCurrent: false, isActive: true },
                                    { rank: displayedLevel, isCurrent: true, isActive: true },
                                    { rank: adjacent.right1, isCurrent: false, isActive: false }
                                ];

                                return nodes.map((node, index) => {
                                    const isCurrent = node.isCurrent;
                                    const isActive = node.isActive;
                                    const levelLabel = getTierBadge(node.rank);

                                    return (
                                        <div key={index} className="flex flex-col items-center transition-transform" style={{
                                            transform: `translateY(${isCurrent ? '20px' : '5px'})`
                                        }}>
                                            <div
                                                className={`
                                                    ${isCurrent ? 'flex items-center justify-center shadow-[0_0_20px_rgba(91,71,205,0.6)] bg-cover bg-center' : `size-12 bg-gray-950/80 border ${isActive ? 'border-[#9770DD]/40' : 'border-white/10'}`}
                                                    rounded-full flex items-center justify-center relative
                                                `}
                                                style={isCurrent ? { backgroundImage: `url(${PurpleCircleFrameImg})`, width: '85px', height: '85px' } : {}}
                                            >
                                                <span className={`font-bold ${isCurrent ? 'text-white text-2xl' : isActive ? 'text-white/70 text-sm' : 'text-white/30 text-sm'}`}>
                                                    {levelLabel}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div> */}

                    {/* Profile Hexagon */}
                    <div className="size-24 mb-4 relative flex items-center justify-center mt-4">
                        <img src={UserProfileIcon} alt="Profile" className="w-full h-full object-cover rounded-full relative z-10" />
                    </div>

                    <h2 className="text-2xl font-semibold text-white mb-2">
                        {viewingAddress ? t("community.profile") : t("community.myProfile")}
                    </h2>

                    {/* Rank Badge */}
                    <div className="flex items-center gap-1 bg-[#1a0b33] border border-[#5B47CD]/50 rounded-full px-4 py-1.5 mb-4 shadow-[0_0_10px_rgba(91,71,205,0.2)]">
                        <span className="text-[#5B47CD] text-[12px]">💎</span>
                        <span className="text-[#5B47CD] font-bold text-sm">{community_ranking || "V 0"}</span>
                        <span className="material-symbols-outlined text-[#5B47CD] text-[14px]">chevron_right</span>
                    </div>

                    {/* Address */}
                    <div className="flex items-center gap-2 mb-8">
                        <span className="text-white/60 font-medium">
                            {viewingAddress || account
                                ? `${(viewingAddress || account).slice(0, 6)}...${(viewingAddress || account).slice(-4)}`
                                : "0x0000...0000"}
                        </span>
                        <FiCopy
                            className="text-white/40 cursor-pointer hover:text-white transition-colors"
                            onClick={() => copyAddress(groupSales.address || account || "")}
                        />
                    </div>

                    {/* Team Stats */}
                    <div className="w-full bg-[#0a0610]/80 rounded-[20px] border border-white/10 p-4 flex divide-x divide-white/10 mb-6 relative overflow-hidden">

                        <div className="flex-1 flex items-center justify-center gap-4 py-2">
                            <div className="size-10 rounded-full flex items-center justify-center text-white">
                                <HiUser className="size-10" />
                            </div>
                            <div>
                                <p className="text-white/40 text-[10px] font-bold tracking-wider mb-1">{t("community.direct").toUpperCase()}</p>
                                <p className="text-white font-bold text-lg leading-none">
                                    {directCountFromContract != null ? directCountFromContract : rewardData.directCount}
                                </p>
                            </div>
                        </div>
                        <div className="flex-1 flex items-center justify-center gap-4 py-2">
                            <div className="size-10 rounded-full flex items-center justify-center text-white">
                                <HiUserGroup className="size-10" />
                            </div>
                            <div>
                                <p className="text-white/40 text-[10px] font-bold tracking-wider mb-1">{t("community.community").toUpperCase()}</p>
                                <p className="text-white font-bold text-lg leading-none">
                                    {communityCountFromContract != null ? communityCountFromContract : rewardData.communityCount}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Total Buypack Bonus */}
                    <div className="w-full bg-[#0a0610]/80 rounded-[20px] border border-white/10 p-4 flex flex-col mb-4">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="material-symbols-outlined text-white/50 text-[18px]">info</span>
                            <span className="text-white/50 text-sm font-medium">
                                {activeTab === "pack" ? t("community.totalBuypackBonus") : t("community.totalMaxCap")}
                            </span>
                        </div>
                        <div className="flex justify-between items-end min-w-0">
                            <div className="flex items-baseline gap-1.5 min-w-0">
                                <span className="text-white text-[21px] min-[380px]:text-[22px] sm:text-3xl font-bold leading-none tracking-tight">
                                    {activeTab == "pack" ? (
                                        totalBonusFromContract != null ? formatBonusAmount(totalBonusFromContract) : "0.00"
                                    ) : (
                                        totalStakingBonusFromContract != null ? totalStakingBonusFromContract : "0.00"
                                    )}
                                </span>
                                <span className="text-white/80 text-[11px] sm:text-sm font-bold shrink-0">USDT</span>
                            </div>
                            {activeTab === "pack" && (
                                <button onClick={() => account && navigate("/bonus-history")} className="flex items-center gap-2 bg-[#1a0b33] border border-[#5B47CD]/30 rounded-lg px-3 py-1.5 hover:bg-[#5B47CD]/20 transition-colors">
                                    <span className="text-white text-xs font-bold">{t("community.bonusHistory")}</span>
                                    <span className="material-symbols-outlined text-white text-[14px]">chevron_right</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Sales Cards */}
                    <div className="w-full flex gap-3">
                        {/* Personal Sales / Small Zone */}
                        <div className="flex-1 bg-[#0a0610]/80 rounded-[20px] border border-white/10 p-4 flex flex-col relative overflow-hidden group hover:border-[#5B47CD]/40 transition-colors">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="material-symbols-outlined text-white text-[18px]">stars</span>
                                <span className="text-white/50 text-sm font-medium">
                                    {activeTab === "staking" ? t("community.smallZoneStaking") : t("community.personalSales")}
                                </span>
                            </div>
                            <div className="flex items-baseline gap-1.5 mb-8">
                                <span className="text-white text-[16px] min-[380px]:text-[17px] sm:text-2xl font-bold leading-tight tracking-tight">
                                    {activeTab === "staking"
                                        ? parseFloat(String(smallZoneData || 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        : parseFloat(String(groupSales.amount || 0).replace(/,/g, "")).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    }
                                </span>
                                <span className="text-white/80 text-[10px] sm:text-xs font-bold shrink-0">USDT</span>
                            </div>
                            {/* Decorative Chart Line */}
                            <div className="absolute bottom-4 left-0 w-full px-4">
                                <svg width="100%" height="20" viewBox="0 0 100 20" preserveAspectRatio="none">
                                    <path d="M0,15 Q10,5 20,10 T40,15 T60,5 T80,10 T100,15" fill="none" stroke="#5B47CD" strokeWidth="2" strokeLinecap="round" className="opacity-80 group-hover:opacity-100 transition-opacity" />
                                </svg>
                            </div>
                        </div>

                        {/* Group Sales */}
                        <div className="flex-1 bg-[#0a0610]/80 rounded-[20px] border border-white/10 p-4 flex flex-col relative overflow-hidden group hover:border-[#5B47CD]/40 transition-colors">
                            <div className="flex items-center gap-2 mb-4">
                                <span className="material-symbols-outlined text-white text-[18px]">group_work</span>
                                <span className="text-white/50 text-sm font-medium">
                                    {activeTab === "staking" ? t("community.groupSalesStaking") : t("community.groupSales")}
                                </span>
                            </div>
                            <div className="flex items-baseline gap-1.5 mb-8">
                                <span className="text-white text-[16px] min-[380px]:text-[17px] sm:text-2xl font-bold leading-tight tracking-tight">
                                    {activeTab === "staking"
                                        ? parseFloat(String(GroupStakingData || 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                        : parseFloat(String(groupSales.totalVolume || 0).replace(/,/g, "")).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    }
                                </span>
                                <span className="text-white/80 text-[10px] sm:text-xs font-bold shrink-0">USDT</span>
                            </div>
                            {/* Decorative Chart Line */}
                            <div className="absolute bottom-4 left-0 w-full px-4">
                                <svg width="100%" height="20" viewBox="0 0 100 20" preserveAspectRatio="none">
                                    <path d="M0,10 Q15,0 30,12 T60,8 T85,15 T100,5" fill="none" stroke="#5B47CD" strokeWidth="2" strokeLinecap="round" className="opacity-80 group-hover:opacity-100 transition-opacity" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Ranking Cards */}
                    <div className="w-full flex gap-3 mt-4">
                        <div className="flex-1 bg-gradient-to-br from-[#1a1023]/90 to-[#0a0610]/90 rounded-[22px] border border-white/10 p-4 flex flex-col relative overflow-hidden group hover:border-[#9770DD]/40 transition-all duration-300">
                            <div className="flex items-center gap-2 mb-3 relative z-10">
                                <div className="size-8 rounded-lg bg-[#9770DD]/20 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-[#9770DD] text-[18px]">social_leaderboard</span>
                                </div>
                                <span className="text-white/50 text-[10px] font-bold tracking-wider uppercase">
                                    {t("community.roiActiveRank") || "Booster bonus"}
                                </span>
                            </div>
                            <div className="relative z-10">
                                <p className="text-white text-xl sm:text-2xl font-black tracking-tight">
                                    {staking_ranking || "0%"}
                                </p>
                            </div>
                            {/* Decorative background icon */}
                            <span className="material-symbols-outlined absolute -right-2 -bottom-4 text-[80px] text-white/[0.03] pointer-events-none group-hover:text-[#9770DD]/[0.05] transition-colors">
                                social_leaderboard
                            </span>
                        </div>
                        <div className="flex-1 bg-gradient-to-br from-[#1a1023]/90 to-[#0a0610]/90 rounded-[22px] border border-white/10 p-4 flex flex-col relative overflow-hidden group hover:border-[#9770DD]/40 transition-all duration-300">
                            <div className="flex items-center gap-2 mb-3 relative z-10">
                                <div className="size-8 rounded-lg bg-[#5B47CD]/20 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-[#5B47CD] text-[18px]">workspace_premium</span>
                                </div>
                                <span className="text-white/50 text-[10px] font-bold tracking-wider uppercase">
                                    {t("community.generationRank") || "Growth Rank"}
                                </span>
                            </div>
                            <div className="relative z-10">
                                <p className="text-white text-xl sm:text-2xl font-black tracking-tight">
                                    {generation_rank || formatGenerationRank(0)}
                                </p>
                            </div>
                            {/* Decorative background icon */}
                            <span className="material-symbols-outlined absolute -right-2 -bottom-4 text-[80px] text-white/[0.03] pointer-events-none group-hover:text-[#5B47CD]/[0.05] transition-colors">
                                workspace_premium
                            </span>
                        </div>
                    </div>

                </div>
                {/* --- New Tabs & Lists UI --- */}
                <div className="w-full max-w-[550px] mx-auto mt-8 mb-6 px-4 relative z-20">
                    {/* Community Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <span className="material-symbols-outlined text-white text-[28px]">groups</span>
                        <h2 className="text-2xl font-semibold text-white flex items-center gap-3">
                            Community
                            <span className="text-white/40 font-normal text-lg">•</span>
                            <span className="text-white/90">{communityMembers.length || 0}</span>
                        </h2>
                    </div>

                    {/* Custom Tabs */}
                    <div className="flex items-center mb-8 bg-[#0a0610] rounded-2xl border border-white/10 p-1.5">
                        <button
                            onClick={() => {
                                setActiveTab("pack");
                                setCurrentPage(1);
                                if (!packData.length && account) {
                                    fetchCommunityMembers(viewingAddress || account);
                                }
                            }}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all ${activeTab === 'pack' ? 'bg-gradient-to-r from-[#7a5bd5] to-[#5a42b1] text-white shadow-lg' : 'text-white/50 hover:text-white'}`}
                        >
                            <span className="material-symbols-outlined text-[20px]">inventory_2</span>
                            <span>{t('community.pack')}</span>
                        </button>
                        <button
                            onClick={async () => {
                                setActiveTab("staking");
                                setCurrentPage(1);
                                if (!stakingData.length && account) {
                                    if (!communityMembers.length) {
                                        await fetchCommunityMembers(viewingAddress || account);
                                    }
                                    fetchStakingData(viewingAddress || account);
                                }
                            }}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all ${activeTab === 'staking' ? 'bg-[#1a1023] border border-white/10 text-white shadow-lg' : 'text-white/50 hover:text-white'}`}
                        >
                            <span className="material-symbols-outlined text-[20px]">layers</span>
                            <span>{t('community.staking')}</span>
                        </button>
                    </div>

                    {/* Sub-header for the list */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-white text-2xl" style={{ transform: activeTab === 'pack' ? 'rotate(-15deg)' : 'none' }}>
                                {activeTab === 'pack' ? 'style' : 'lock'}
                            </span>
                            <h3 className="text-white font-semibold text-xl flex items-center gap-3">
                                {activeTab === 'pack' ? 'Pack' : 'Staking'}
                                <span className="text-white/40 font-normal text-lg">•</span>
                            </h3>
                        </div>
                        <div className="text-white/70 text-base">
                            Total {activeTab === 'pack' ? communityMembers.length : stakingData.length} {activeTab === 'pack' ? 'Packs' : 'Stakes'}
                        </div>
                    </div>

                    {/* List Cards */}
                    <div className="space-y-4 pb-10">
                        {activeTab === "pack" && (() => {
                            const dataList = activeTab === 'pack' ? communityMembers : stakingData;
                            const totalItems = dataList.length;
                            const totalPages = Math.ceil(totalItems / itemsPerPage);
                            const startIndex = (currentPage - 1) * itemsPerPage;
                            const endIndex = startIndex + itemsPerPage;
                            const currentItems = dataList.slice(startIndex, endIndex);

                            if (totalItems === 0) {
                                return (
                                    <div className="text-center text-white/50 py-8">
                                        {isLoading || isLoadingStaking ? t('common.loading') : t('common.noData')}
                                    </div>
                                );
                            }

                            return (
                                <>
                                    {currentItems.map((member, index) => (
                                        <div
                                            key={member.id || index}
                                            onClick={async () => {
                                                if (activeTab === 'pack') {
                                                    await viewMemberCommunity(member.address, member.level);
                                                } else {
                                                    await viewMemberCommunity(member.address, member.level);
                                                }
                                            }}
                                            className="bg-[#0a0610]/80 rounded-[22px] border border-white/10 p-3 flex items-center gap-3 cursor-pointer hover:border-[#5B47CD]/50 transition-colors"
                                        >
                                            {/* Left Badge */}
                                            <div className="size-[54px] relative flex-shrink-0 flex items-center justify-center">
                                                <img src={PurpleCircleFrameImg} alt="Frame" className="absolute inset-0 w-full h-full object-cover" />
                                                <div className="size-9 bg-[#241630] rounded-full flex items-center justify-center relative z-10 border border-[#9770DD] shadow-inner">
                                                    <span className="material-symbols-outlined text-white/80">person</span>
                                                </div>
                                            </div>

                                            {/* Center Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                                    <span className="text-white font-bold text-[15px] leading-tight truncate max-w-[120px]">{member.address.slice(0, 6)}...{member.address.slice(-4)}</span>
                                                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-950 border border-white/20 shrink-0">
                                                        <span className="material-symbols-outlined text-white text-[12px]">signal_cellular_alt</span>
                                                        <span className="text-white font-bold text-[10px]">P{member.level?.replace('S', '') || '9'}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-white/40 text-[10px] truncate max-w-[105px]">
                                                        {member.address.slice(0, 6)}...{member.address.slice(-4)}
                                                    </span>
                                                    <FiCopy
                                                        className="text-white/40 size-3 cursor-pointer hover:text-white transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (member.address) {
                                                                copyAddress(member.address)
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Right Info */}
                                            <div className="w-[92px] flex-shrink-0 flex flex-col items-end justify-center gap-0.5 border-l border-white/10 pl-3 py-1 text-right">
                                                <span className="material-symbols-outlined text-[#DFD2FE] text-[14px]">change_circle</span>
                                                <div className="flex items-baseline justify-end gap-0.5 max-w-full">
                                                    <span className="text-white font-bold text-[12px] leading-tight truncate max-w-[64px]">
                                                        {activeTab === 'staking' ? member.totalAmount : (member.amount || '0.00')}
                                                    </span>
                                                    <span className="text-white/60 text-[9px] font-bold shrink-0">USD</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Pagination Controls */}
                                    {totalItems > itemsPerPage && (
                                        <div className="flex items-center justify-center gap-4 mt-6">
                                            <button
                                                type="button"
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                className="size-10 rounded-xl bg-[#0a0610] border border-white/10 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                <span className="material-symbols-outlined">chevron_left</span>
                                            </button>
                                            <span className="text-white/70 text-sm font-medium">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                className="size-10 rounded-xl bg-[#0a0610] border border-white/10 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                <span className="material-symbols-outlined">chevron_right</span>
                                            </button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                        {/* Staking Tab Content */}
                        {activeTab === "staking" && (() => {
                            const totalItems = stakingData.length;
                            const totalPages = Math.ceil(totalItems / itemsPerPage);
                            const startIndex = (currentPage - 1) * itemsPerPage;
                            const endIndex = startIndex + itemsPerPage;
                            const currentItems = stakingData.slice(startIndex, endIndex);
                            const showPagination = totalItems > itemsPerPage;

                            if (totalItems === 0) {
                                return (
                                    <div className="text-center text-white/50 py-8">
                                        {isLoadingStaking ? t('common.loading') : t('common.noData')}
                                    </div>
                                );
                            }

                            return (
                                <>
                                    {currentItems.map((member, index) => (
                                        <div
                                            key={member.id || index}
                                            onClick={async () => {
                                                await viewMemberCommunity(member.address, member.level);
                                            }}
                                            className="bg-[#0a0610]/80 rounded-[22px] border border-white/10 p-3 flex items-center gap-3 cursor-pointer hover:border-[#5B47CD]/50 transition-colors"
                                        >
                                            {/* Left Badge */}
                                            <div className="size-[54px] relative flex-shrink-0 flex items-center justify-center">
                                                <img src={PurpleCircleFrameImg} alt="Frame" className="absolute inset-0 w-full h-full object-cover" />
                                                <div className="size-9 bg-[#241630] rounded-full flex items-center justify-center relative z-10 border border-[#9770DD] shadow-inner">
                                                    <span className="material-symbols-outlined text-white/80">person</span>
                                                </div>
                                            </div>

                                            {/* Center Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                                    <span className="text-white font-bold text-[15px] leading-tight truncate max-w-[120px]">{member.address.slice(0, 6)}...{member.address.slice(-4)}</span>
                                                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-950 border border-white/20 shrink-0">
                                                        <span className="material-symbols-outlined text-white text-[12px]">signal_cellular_alt</span>
                                                        <span className="text-white font-bold text-[10px]">{member.level}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-white/40 text-[10px] truncate max-w-[105px]">
                                                        {member.address.slice(0, 6)}...{member.address.slice(-4)}
                                                    </span>
                                                    <FiCopy
                                                        className="text-white/40 size-3 cursor-pointer hover:text-white transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (member.address) {
                                                                copyAddress(member.address)
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Right Info */}
                                            <div className="w-[92px] flex-shrink-0 flex flex-col items-end justify-center gap-0.5 border-l border-white/10 pl-3 py-1 text-right">
                                                <span className="material-symbols-outlined text-[#DFD2FE] text-[14px]">change_circle</span>
                                                <div className="flex items-baseline justify-end gap-0.5 max-w-full">
                                                    <span className="text-white font-bold text-[12px] leading-tight truncate max-w-[64px]">
                                                        {member.totalStake}
                                                    </span>
                                                    <span className="text-white/60 text-[9px] font-bold shrink-0">USD</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Pagination Controls */}
                                    {totalItems > itemsPerPage && (
                                        <div className="flex items-center justify-center gap-4 mt-6">
                                            <button
                                                type="button"
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                className="size-10 rounded-xl bg-[#0a0610] border border-white/10 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                <span className="material-symbols-outlined">chevron_left</span>
                                            </button>
                                            <span className="text-white/70 text-sm font-medium">
                                                Page {currentPage} of {totalPages}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                className="size-10 rounded-xl bg-[#0a0610] border border-white/10 flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                <span className="material-symbols-outlined">chevron_right</span>
                                            </button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
}
